import { useTranslation } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import {
  getTemplateApiErrorMessage,
  isDuplicateTemplateNameError,
} from 'api/page-template/errors';
import { useCallback, useRef, useState } from 'react';
import { TemplateNameConflictModal } from './template-name-conflict-modal';

/**
 * Re-runs the save with the chosen name. Resolves to `true` when the save was
 * actually performed (so the modal can close), or `false` when it short-circuited
 * before saving (so the modal stays open). Rejects when the save fails.
 */
type RetrySave = (newName: string) => Promise<boolean>;

/**
 * Handles the "a template with the same name already exists" (HTTP 400) error
 * returned by the page-template create/update endpoints.
 *
 * Usage: pass the page's `performSave(newName)` callback to the hook, and in a
 * save handler's catch block call `handleConflict(error, name)`. When the error
 * is a duplicate-name conflict it opens a modal that lets the user pick a new
 * title and re-runs `performSave(newName)`; otherwise it returns false so the
 * caller can handle the error normally. Render the returned `conflictModal`
 * somewhere in the component tree.
 *
 * `performSave` is kept in a ref that is refreshed every render, so the retry
 * always uses the latest callback (and therefore the latest page state) rather
 * than a stale closure captured when the conflict was first detected.
 */
export const useTemplateNameConflictModal = (performSave: RetrySave) => {
  const defaultDuplicateMessage = useTranslation(
    'template_name_conflict.duplicate',
  );
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const performSaveRef = useRef<RetrySave>(performSave);

  performSaveRef.current = performSave;

  const close = useCallback(() => {
    setIsOpen(false);
    setName('');
    setErrorMessage(undefined);
    setIsSubmitting(false);
  }, []);

  const handleConflict = useCallback(
    (error: unknown, attemptedName: string): boolean => {
      if (!isDuplicateTemplateNameError(error)) {
        return false;
      }
      setName(attemptedName ?? '');
      setErrorMessage(
        getTemplateApiErrorMessage(error) ?? defaultDuplicateMessage,
      );
      setIsSubmitting(false);
      setIsOpen(true);

      return true;
    },
    [defaultDuplicateMessage],
  );

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();

    if (trimmedName.length === 0) {
      return;
    }
    setIsSubmitting(true);
    try {
      const saved = await performSaveRef.current(trimmedName);

      if (saved) {
        close();
      } else {
        // Nothing was saved (e.g. preconditions not met) — keep the modal open.
        setIsSubmitting(false);
      }
    } catch (error) {
      if (isDuplicateTemplateNameError(error)) {
        setErrorMessage(
          getTemplateApiErrorMessage(error) ?? defaultDuplicateMessage,
        );
        setIsSubmitting(false);
      } else {
        logger.error('Error retrying template save with new name', { error });
        close();
      }
    }
  }, [close, defaultDuplicateMessage, name]);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    setErrorMessage(undefined);
  }, []);

  const conflictModal = (
    <TemplateNameConflictModal
      open={isOpen}
      name={name}
      errorMessage={errorMessage}
      isSubmitting={isSubmitting}
      onNameChange={handleNameChange}
      onCancel={close}
      onSave={handleSave}
    />
  );

  return { handleConflict, conflictModal };
};
